import type { Finding } from '@wholisphere/vpat';
import { useEffect, useState } from 'preact/hooks';
import {
  type ApprovalSummary,
  DashboardApi,
  HttpError,
  type ProductSummary,
  type VpatShareLinkCreated,
  type VpatShareLinkSummary,
} from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

/**
 * Every standard the /v1/vpat route accepts today. Grouped visually so the
 * selector doesn't become a flat 9-checkbox wall. Order matters — we show
 * WCAG 2.2 first (current standard), then 2.1 (DOJ reference for ADA),
 * then the regional frameworks.
 */
type StandardId =
  | 'WCAG 2.2 Level A'
  | 'WCAG 2.2 Level AA'
  | 'WCAG 2.2 Level AAA'
  | 'WCAG 2.1 Level A'
  | 'WCAG 2.1 Level AA'
  | 'WCAG 2.1 Level AAA'
  | 'EN 301 549'
  | 'Section 508';

interface StandardOption {
  id: StandardId;
  label: string;
  hint?: string;
}

const STANDARD_GROUPS: { heading: string; items: StandardOption[] }[] = [
  {
    heading: 'WCAG 2.2 (current spec)',
    items: [
      { id: 'WCAG 2.2 Level A', label: 'Level A', hint: 'Minimum' },
      { id: 'WCAG 2.2 Level AA', label: 'Level AA', hint: 'Implies A; industry baseline' },
      {
        id: 'WCAG 2.2 Level AAA',
        label: 'Level AAA',
        hint: 'Implies A + AA; partial-support common',
      },
    ],
  },
  {
    heading: 'WCAG 2.1 (DOJ ADA reference)',
    items: [
      { id: 'WCAG 2.1 Level A', label: 'Level A' },
      { id: 'WCAG 2.1 Level AA', label: 'Level AA', hint: 'DOJ Title III default' },
      { id: 'WCAG 2.1 Level AAA', label: 'Level AAA' },
    ],
  },
  {
    heading: 'Regional frameworks',
    items: [
      { id: 'Section 508', label: 'Section 508', hint: 'US federal — adds Chapter 3 block' },
      { id: 'EN 301 549', label: 'EN 301 549', hint: 'EU — adds Annex A block' },
    ],
  },
];

type Edition = 'INT' | 'WCAG' | '508' | 'EU';

const DEFAULT_EDITION: Edition = 'INT';

interface EditionOption {
  id: Edition;
  label: string;
  subtitle: string;
  /**
   * Standards the edition REQUIRES — these checkboxes are auto-checked + disabled.
   * Basis: ITI + W3C semantic floors.
   *   - 508 edition: Section 508 E205 delegates to WCAG 2.0 AA, so both are structural.
   *   - EU edition: EN 301 549 Clause 9 delegates to WCAG 2.1 AA, so both are structural.
   *   - INT: only WCAG 2.2 AA is structurally required; 508 + EN 301 549 are optional.
   *   - WCAG: just WCAG 2.2 AA.
   */
  lockedStandards: StandardId[];
  /** Standards pre-checked on top of the locks when the edition is picked. */
  extraPresetStandards: StandardId[];
  /**
   * Standards the edition's output will suppress even if checked — the checkbox
   * gets disabled with an explanatory hint so users learn which edition to pick
   * instead.
   */
  suppressedStandards: StandardId[];
}

const EDITIONS: EditionOption[] = [
  {
    id: 'INT',
    label: 'VPAT 2.5 INT',
    subtitle: 'International — WCAG + 508 + EN 301 549 all shown',
    lockedStandards: ['WCAG 2.2 Level AA'],
    extraPresetStandards: ['Section 508', 'EN 301 549'],
    suppressedStandards: [],
  },
  {
    id: 'WCAG',
    label: 'VPAT 2.5 WCAG',
    subtitle: 'WCAG-only — no 508 or EN 301 549 blocks',
    lockedStandards: ['WCAG 2.2 Level AA'],
    extraPresetStandards: [],
    suppressedStandards: ['Section 508', 'EN 301 549'],
  },
  {
    id: '508',
    label: 'VPAT 2.5 Rev 508',
    subtitle: 'US federal procurement — Chapter 3 leads',
    lockedStandards: ['WCAG 2.2 Level AA', 'Section 508'],
    extraPresetStandards: [],
    suppressedStandards: [],
  },
  {
    id: 'EU',
    label: 'VPAT 2.5 EU',
    subtitle: 'EU public sector + EAA — Annex A leads',
    lockedStandards: ['WCAG 2.2 Level AA', 'EN 301 549'],
    extraPresetStandards: [],
    suppressedStandards: [],
  },
];

/** Full preset = locked + extras. Locked come first so they render as leading checkboxes. */
const presetFor = (edition: Edition): StandardId[] => {
  const opt = EDITIONS.find((e) => e.id === edition);
  if (!opt) return ['WCAG 2.2 Level AA'];
  return [...opt.lockedStandards, ...opt.extraPresetStandards];
};

const DEFAULT_STANDARDS: StandardId[] = presetFor(DEFAULT_EDITION);

/**
 * Fallback findings used only when no real scan exists for the org. Once a
 * scan completes, the dashboard pulls findings via /v1/findings/latest and
 * the VPAT generates from real evidence.
 */
const SAMPLE_FINDINGS: Finding[] = [
  {
    criterion: '1.1.1',
    conformance: 'Supports',
    remarks: 'Sample finding (no real scan yet — run one from the agent or POST to /v1/findings).',
    evidence: [],
  },
  {
    criterion: '1.4.3',
    conformance: 'Partially Supports',
    remarks: 'Sample finding (no real scan yet).',
    evidence: [],
  },
  {
    criterion: '4.1.2',
    conformance: 'Supports',
    remarks: 'Sample finding (no real scan yet — agent derives N/R/V regardless of host markup).',
    evidence: [],
  },
];

export function Compliance({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [productName, setProductName] = useState(org.name);
  const [productVersion, setProductVersion] = useState('1.0.0');
  const [contact, setContact] = useState('a11y@example.test');
  const [edition, setEdition] = useState<Edition>(DEFAULT_EDITION);
  const [standards, setStandards] = useState<StandardId[]>(DEFAULT_STANDARDS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [realFindings, setRealFindings] = useState<Finding[] | null>(null);
  const [scanMeta, setScanMeta] = useState<{
    id: string;
    ts: string;
    source: string;
    findingCount: number;
  } | null>(null);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [findingsError, setFindingsError] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductSummary[] | null>(null);
  const [selectedProductSlug, setSelectedProductSlug] = useState<string>('');
  const [latestApproval, setLatestApproval] = useState<ApprovalSummary | null>(null);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [approveSuccess, setApproveSuccess] = useState<ApprovalSummary | null>(null);
  const [shareLinks, setShareLinks] = useState<VpatShareLinkSummary[] | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [creatingShare, setCreatingShare] = useState(false);
  const [shareExpiresInDays, setShareExpiresInDays] = useState(30);
  const [shareLabel, setShareLabel] = useState('');
  const [shareCreated, setShareCreated] = useState<VpatShareLinkCreated | null>(null);

  const currentEditionOpt = EDITIONS.find((e) => e.id === edition);
  const lockedSet = new Set<StandardId>(currentEditionOpt?.lockedStandards ?? []);
  const suppressedSet = new Set<StandardId>(currentEditionOpt?.suppressedStandards ?? []);

  // Load the org's active products so the picker can replace free-text input.
  useEffect(() => {
    let cancelled = false;
    api
      .listProducts(org.id, false)
      .then((r) => {
        if (!cancelled) setProducts(r.products);
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [org.id]);

  // Load the active approval for the latest scan whenever the scan changes,
  // so the UI can show "currently signed by X on Y" + a Revoke button.
  useEffect(() => {
    if (!scanMeta) {
      setLatestApproval(null);
      return;
    }
    let cancelled = false;
    api
      .latestApprovalForScan(org.id, scanMeta.id)
      .then((r) => {
        if (!cancelled) setLatestApproval(r.approval);
      })
      .catch(() => {
        if (!cancelled) setLatestApproval(null);
      });
    return () => {
      cancelled = true;
    };
  }, [org.id, scanMeta?.id]);

  const approveVpat = async (): Promise<void> => {
    if (!scanMeta) return;
    const selected = selectedProductSlug
      ? products?.find((p) => p.slug === selectedProductSlug)
      : undefined;
    const vendorName = selected?.defaultVendorName ?? org.name;
    const description =
      selected?.defaultProductDescription ??
      `${productName} — accessibility conformance via Wholisphere.`;
    const note = window.prompt(
      'Approval notes (optional, e.g. "Reviewed against latest scan, ready for procurement"):',
      '',
    );
    setApproving(true);
    setApproveError(null);
    setApproveSuccess(null);
    try {
      const approval = await api.createApproval(org.id, {
        scanId: scanMeta.id,
        edition,
        standards,
        vendorName,
        contactInfo: contact,
        productDescription: description,
        ...(note && note.trim().length > 0 ? { notes: note.trim() } : {}),
      });
      setApproveSuccess(approval);
      setLatestApproval(approval);
    } catch (e) {
      setApproveError(e instanceof HttpError ? e.message : String(e));
    } finally {
      setApproving(false);
    }
  };

  // Load share links for the current scan whenever the scan changes.
  useEffect(() => {
    if (!scanMeta) {
      setShareLinks(null);
      return;
    }
    let cancelled = false;
    api
      .listVpatShareLinks(org.id)
      .then((r) => {
        if (cancelled) return;
        // Filter to share links pointing at the current scan only — keeps
        // the list focused; full org-wide list lives elsewhere.
        setShareLinks(r.shareLinks.filter((s) => s.scanId === scanMeta.id));
      })
      .catch(() => {
        if (!cancelled) setShareLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [org.id, scanMeta?.id]);

  const refreshShareLinks = async (): Promise<void> => {
    if (!scanMeta) return;
    try {
      const r = await api.listVpatShareLinks(org.id);
      setShareLinks(r.shareLinks.filter((s) => s.scanId === scanMeta.id));
    } catch (e) {
      setShareError(e instanceof HttpError ? e.message : String(e));
    }
  };

  const createShareLink = async (): Promise<void> => {
    if (!scanMeta) return;
    const selected = selectedProductSlug
      ? products?.find((p) => p.slug === selectedProductSlug)
      : undefined;
    const vendorName = selected?.defaultVendorName ?? org.name;
    const description =
      selected?.defaultProductDescription ??
      `${productName} — accessibility conformance via Wholisphere.`;
    setCreatingShare(true);
    setShareError(null);
    setShareCreated(null);
    try {
      const result = await api.createVpatShareLink(org.id, {
        scanId: scanMeta.id,
        edition,
        standards,
        vendorName,
        contactInfo: contact,
        productDescription: description,
        ...(shareLabel.trim().length > 0 ? { label: shareLabel.trim() } : {}),
        expiresInDays: shareExpiresInDays,
      });
      setShareCreated(result);
      setShareLabel('');
      await refreshShareLinks();
    } catch (e) {
      setShareError(e instanceof HttpError ? e.message : String(e));
    } finally {
      setCreatingShare(false);
    }
  };

  const revokeShareLink = async (id: string): Promise<void> => {
    if (
      !confirm(
        'Revoke this share link? Anyone holding the URL will no longer be able to view the VPAT.',
      )
    )
      return;
    setShareError(null);
    try {
      await api.revokeVpatShareLink(org.id, id);
      await refreshShareLinks();
    } catch (e) {
      setShareError(e instanceof HttpError ? e.message : String(e));
    }
  };

  const copyShareUrl = async (url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback: select the text in the visible field for manual copy.
    }
  };

  // Picking a product auto-fills name + edition + standards + cover-page defaults.
  const pickProduct = (slug: string): void => {
    setSelectedProductSlug(slug);
    if (!slug || !products) return;
    const p = products.find((pr) => pr.slug === slug);
    if (!p) return;
    setProductName(p.name);
    setEdition(p.defaultEdition);
    setStandards(p.defaultStandards as StandardId[]);
    if (p.defaultContactInfo) setContact(p.defaultContactInfo);
  };

  useEffect(() => {
    let cancelled = false;
    setFindingsLoading(true);
    setFindingsError(null);
    api
      .latestFindings(productName, productVersion)
      .then((res) => {
        if (cancelled) return;
        if (res.scan && res.findings.length > 0) {
          setRealFindings(res.findings);
          setScanMeta({
            id: res.scan.id,
            ts: res.scan.ts,
            source: res.scan.source,
            findingCount: res.findings.length,
          });
        } else {
          setRealFindings(null);
          setScanMeta(null);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setFindingsError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setFindingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productName, productVersion]);

  const findingsForVpat = realFindings ?? SAMPLE_FINDINGS;
  const usingSample = realFindings === null;

  const pickEdition = (next: Edition) => {
    setEdition(next);
    setStandards(presetFor(next));
  };

  const toggleStandard = (id: StandardId) => {
    // Locked + suppressed are non-toggleable. This is a belt for the disabled
    // checkbox; the checkbox itself also has `disabled`.
    if (lockedSet.has(id) || suppressedSet.has(id)) return;
    setStandards((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id].sort(),
    );
  };

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      if (standards.length === 0) {
        setError('Pick at least one standard to report against.');
        setBusy(false);
        return;
      }
      const selectedProduct = selectedProductSlug
        ? products?.find((p) => p.slug === selectedProductSlug)
        : undefined;
      const vendorName = selectedProduct?.defaultVendorName ?? org.name;
      const productDescription =
        selectedProduct?.defaultProductDescription ??
        `${productName} — accessibility conformance via Wholisphere.`;
      const result = await api.vpat({
        product: {
          productName,
          productVersion,
          vendorName,
          contactInfo: contact,
          reportDate: new Date().toISOString().slice(0, 10),
          productDescription,
          standards,
          edition,
          notes: usingSample
            ? 'Sample findings used — run a scan or POST findings to /v1/findings for a real VPAT.'
            : 'Findings combine automated rule evaluation with AI-augmented analysis and human review.',
        },
        findings: findingsForVpat,
      });
      const blob = new Blob([result.markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${productName.replace(/\s+/g, '-')}-vpat-${result.meta.version}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div class="page-header">
        <h1>Compliance</h1>
        <p>Generate a court-defensible VPAT 2.5 INT for procurement, legal, or RFP responses.</p>
      </div>

      <div class="card">
        <h2>Generate VPAT</h2>
        {findingsLoading && (
          <p class="desc small muted">
            <output>Loading latest findings…</output>
          </p>
        )}
        {findingsError && (
          <Banner variant="warn">
            Could not load latest findings: {findingsError}. Falling back to sample data.
          </Banner>
        )}
        {!findingsLoading && !findingsError && scanMeta && (
          <p class="desc">
            Using <strong>{scanMeta.findingCount}</strong> findings from the most recent{' '}
            <code>{scanMeta.source}</code> scan (<code>{scanMeta.id.slice(0, 12)}…</code>,{' '}
            {new Date(scanMeta.ts).toLocaleString()}).
          </p>
        )}
        {!findingsLoading && !findingsError && !scanMeta && (
          <p class="desc">
            No scan found for{' '}
            <strong>
              {productName} {productVersion}
            </strong>
            . Generating with <strong>sample findings</strong> — run a scan or POST findings to{' '}
            <code>/v1/findings</code> for a real VPAT.
          </p>
        )}
        {products && products.length > 0 && (
          <div class="field">
            <label htmlFor="vpat-product-picker">Product</label>
            <select
              id="vpat-product-picker"
              value={selectedProductSlug}
              onChange={(e) => pickProduct((e.target as HTMLSelectElement).value)}
            >
              <option value="">— pick to auto-fill, or type a free-text name below —</option>
              {products.map((p) => (
                <option key={p.id} value={p.slug}>
                  {p.name} ({p.slug})
                </option>
              ))}
            </select>
            <p class="small muted" style={{ marginTop: 4 }}>
              Selecting a product auto-fills the cover page (name, vendor, contact, edition,
              standards) from its defaults. Manage products under <strong>Products</strong>.
            </p>
          </div>
        )}
        <div class="field">
          <label htmlFor="vpat-product">Product name</label>
          <input
            id="vpat-product"
            type="text"
            value={productName}
            onInput={(e) => setProductName((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label htmlFor="vpat-version">Product version</label>
          <input
            id="vpat-version"
            type="text"
            value={productVersion}
            onInput={(e) => setProductVersion((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label htmlFor="vpat-contact">Contact (shown on the cover page)</label>
          <input
            id="vpat-contact"
            type="email"
            value={contact}
            onInput={(e) => setContact((e.target as HTMLInputElement).value)}
          />
        </div>
        <fieldset class="field standards-fieldset">
          <legend>VPAT edition</legend>
          <p class="small muted" style={{ marginTop: 0 }}>
            ITI publishes four editions. Picking one pre-fills sensible standards + adjusts the
            document's structure (which block leads, which are omitted).
          </p>
          <ul class="editions-list">
            {EDITIONS.map((opt) => (
              <li key={opt.id}>
                <label class="edition-option">
                  <input
                    type="radio"
                    name="vpat-edition"
                    value={opt.id}
                    checked={edition === opt.id}
                    onChange={() => pickEdition(opt.id)}
                  />
                  <span class="edition-body">
                    <span class="edition-label">{opt.label}</span>
                    <span class="edition-subtitle small muted">{opt.subtitle}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
        <fieldset class="field standards-fieldset">
          <legend>Standards to report against</legend>
          <p class="small muted" style={{ marginTop: 0 }}>
            The <strong>{currentEditionOpt?.label ?? edition}</strong> template requires the locked
            entries below; you can add extras on top. Section 508 and EN 301 549 add their own
            report sections alongside the WCAG table. Selecting a higher level implies lower levels
            (AA ⇒ A, AAA ⇒ A + AA).
          </p>
          {STANDARD_GROUPS.map((group) => (
            <div key={group.heading} class="standards-group">
              <div class="standards-group-heading">{group.heading}</div>
              <ul class="standards-list">
                {group.items.map((opt) => {
                  const checked = standards.includes(opt.id);
                  const locked = lockedSet.has(opt.id);
                  const suppressed = suppressedSet.has(opt.id);
                  const disabled = locked || suppressed;
                  const rowClasses = ['standards-option'];
                  if (locked) rowClasses.push('is-locked');
                  if (suppressed) rowClasses.push('is-suppressed');
                  return (
                    <li key={opt.id}>
                      <label class={rowClasses.join(' ')}>
                        <input
                          type="checkbox"
                          checked={checked && !suppressed}
                          disabled={disabled}
                          onChange={() => toggleStandard(opt.id)}
                        />
                        <span class="standards-label">
                          {opt.label}
                          {locked && (
                            <span
                              class="pill pill-locked"
                              aria-label="required by selected edition"
                            >
                              required
                            </span>
                          )}
                          {suppressed && (
                            <span class="pill pill-suppressed">not in this edition</span>
                          )}
                        </span>
                        {opt.hint && !locked && !suppressed && (
                          <span class="standards-hint small muted">{opt.hint}</span>
                        )}
                        {locked && (
                          <span class="standards-hint small muted">
                            {currentEditionOpt?.label} can't be generated without this
                          </span>
                        )}
                        {suppressed && (
                          <span class="standards-hint small muted">
                            Pick the matching edition (Rev 508 / EU) to include this
                          </span>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          <div class="small muted" style={{ marginTop: 4 }}>
            Selected: <code>{standards.length ? standards.join(' · ') : '(none)'}</code>
          </div>
        </fieldset>
        {error && <Banner variant="warn">Failed: {error}</Banner>}
        <button type="button" disabled={busy} onClick={() => void generate()}>
          {busy ? 'Generating…' : 'Generate VPAT (markdown)'}
        </button>
      </div>

      <div class="card">
        <h2>Approval</h2>
        <p class="desc">
          A VPAT approval is a court-defensible attestation. The rendered VPAT is hashed (SHA-256)
          and chained into the audit log; mutating findings re-renders to a new hash, and the prior
          approval row preserves the OLD hash so the trail stays intact.
        </p>
        {!scanMeta && (
          <p class="small muted">
            Approve only against a real scan. Run one via the GitHub Action, the scanner-runner CLI,
            or POST findings to <code>/v1/findings</code>.
          </p>
        )}
        {scanMeta && latestApproval && latestApproval.status === 'approved' && (
          <Banner variant="success" style={{ marginBottom: 12 }}>
            <strong>✓ Currently approved</strong> — hash{' '}
            <code title={latestApproval.vpatHash}>{latestApproval.vpatHash.slice(0, 16)}…</code>,
            edition {latestApproval.edition}, {new Date(latestApproval.approvedAt).toLocaleString()}
            . Manage on the Approvals page.
          </Banner>
        )}
        {approveError && <Banner variant="warn">Approval failed: {approveError}</Banner>}
        {approveSuccess && (
          <Banner variant="success">
            <strong>VPAT approved</strong> — id <code>{approveSuccess.id.slice(0, 12)}…</code>, hash{' '}
            <code title={approveSuccess.vpatHash}>{approveSuccess.vpatHash.slice(0, 16)}…</code>.
            Webhook <code>vpat.approved</code> fired through the outbox.
          </Banner>
        )}
        <button
          type="button"
          disabled={!scanMeta || approving}
          onClick={() => void approveVpat()}
          title={
            !scanMeta
              ? 'Run a real scan first — approvals only attach to real findings.'
              : 'Generate the current VPAT, hash it, and chain the approval into the audit log.'
          }
        >
          {approving ? 'Approving…' : 'Approve this VPAT'}
        </button>
      </div>

      <div class="card">
        <h2>Public share link</h2>
        <p class="desc">
          Generate a signed URL you can email procurement, legal, or an auditor — no Wholisphere
          account needed on their end. Cryptographically signed (32 bytes of entropy) + revocable +
          view-tracked. The plaintext token is shown ONCE at creation; we store only the SHA-256
          hash.
        </p>
        {!scanMeta && (
          <p class="small muted">
            Run + complete a real scan before sharing. Sample data won't be shareable.
          </p>
        )}
        {shareError && <Banner variant="warn">Share link failed: {shareError}</Banner>}
        {shareCreated && (
          <Banner variant="success" style={{ marginBottom: 12 }}>
            <div>
              <strong>Share link created</strong> — copy this URL now, it won't be shown again:
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
              <input
                type="text"
                readOnly
                aria-label="Share link URL"
                value={shareCreated.url}
                style={{ flex: 1, fontFamily: 'monospace' }}
                onFocus={(e) => (e.target as HTMLInputElement).select()}
              />
              <button type="button" onClick={() => void copyShareUrl(shareCreated.url)}>
                Copy
              </button>
            </div>
            <div class="small muted" style={{ marginTop: 6 }}>
              {shareCreated.expiresAt
                ? `Expires ${new Date(shareCreated.expiresAt).toLocaleString()}`
                : 'No expiry — revoke manually when done.'}
            </div>
          </Banner>
        )}
        {scanMeta && (
          <>
            <div class="field">
              <label htmlFor="share-label">Label (optional, audit aid)</label>
              <input
                id="share-label"
                type="text"
                placeholder='e.g. "Acme Corp procurement"'
                value={shareLabel}
                onInput={(e) => setShareLabel((e.target as HTMLInputElement).value)}
              />
            </div>
            <div class="field">
              <label htmlFor="share-expires">Expires in</label>
              <select
                id="share-expires"
                value={String(shareExpiresInDays)}
                onChange={(e) =>
                  setShareExpiresInDays(Number((e.target as HTMLSelectElement).value))
                }
              >
                <option value="7">7 days</option>
                <option value="30">30 days (recommended)</option>
                <option value="90">90 days</option>
                <option value="365">1 year</option>
                <option value="0">No expiry</option>
              </select>
            </div>
            <button type="button" disabled={creatingShare} onClick={() => void createShareLink()}>
              {creatingShare ? 'Generating…' : 'Create share link'}
            </button>
          </>
        )}

        {shareLinks && shareLinks.length > 0 && (
          <table class="data-table" style={{ width: '100%', marginTop: 16 }}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Label</th>
                <th>Created</th>
                <th>Expires</th>
                <th class="num">Views</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shareLinks.map((s) => {
                const revoked = !!s.revokedAt;
                const expired = s.expiresAt ? new Date(s.expiresAt).getTime() <= Date.now() : false;
                const status = revoked ? 'revoked' : expired ? 'expired' : 'active';
                return (
                  <tr key={s.id}>
                    <td>
                      <span class={`pill ${status === 'active' ? 'pill-supports' : 'pill-muted'}`}>
                        {status}
                      </span>
                    </td>
                    <td class="small">{s.label ?? <span class="muted">—</span>}</td>
                    <td class="small muted">{new Date(s.createdAt).toLocaleString()}</td>
                    <td class="small muted">
                      {s.expiresAt ? new Date(s.expiresAt).toLocaleDateString() : '—'}
                    </td>
                    <td class="num">{s.viewCount}</td>
                    <td style={{ textAlign: 'right' }}>
                      {!revoked && !expired && (
                        <button
                          type="button"
                          class="link-button"
                          onClick={() => void revokeShareLink(s.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div class="card">
        <h2>What's in the report</h2>
        <ul style={{ paddingLeft: '20px', margin: 0, lineHeight: 1.8 }}>
          <li>Conformance + remarks per WCAG success criterion at the levels you picked</li>
          <li>Cover page with vendor + product + contact metadata</li>
          <li>Conformance summary table for at-a-glance review</li>
          <li>
            Section 508 Chapter 3 (302 Functional Performance Criteria + E205) — when selected
          </li>
          <li>EN 301 549 Annex A statement of applicability (Clauses 9-13) — when selected</li>
        </ul>
      </div>
    </>
  );
}
